import { useTheme } from '@mui/material/styles'
import React, { FC } from 'react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { LinkButton, OpenNewFieldIcon } from '@sales-center/components'
import AlertTitle from '@mui/material/AlertTitle'
import { ENTITY_TYPES } from '@sales-center/common'
import LinearProgress from '@mui/material/LinearProgress'
import {
  MatchCreateLocationDialogs,
  useMatchCreateLocationFlow,
} from '@user/components/Location/MatchCreateLocation'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle/store'
import {
  createLocationPermission,
  LocationCreatePermOps,
  useCheckPermissions,
} from '@user/store/authorization/permissions'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  showPestPacLocationError?: boolean | null
  addToPestPac?: boolean
}

export const CreateLocationError: FC<Props> = (props) => {
  const { entityType, entityId, showPestPacLocationError } = props
  const tidalThemeActive = useTidalThemeActive()
  const flow = useMatchCreateLocationFlow({ entityId, entityType })
  const { saving, handleCreateNewLocation, handleMatchLocation } = flow
  const title = 'Create Location'

  const theme = useTheme()

  const [canCreateLocations] = useCheckPermissions(
    createLocationPermission as LocationCreatePermOps,
  )

  if (showPestPacLocationError) {
    return (
      <>
        <Box pt={1}>
          <Alert
            sx={{
              display: 'flex',
              alignItems: 'start',
            }}
            severity="error"
          >
            <AlertTitle
              sx={{
                fontSize: '16px',
                color: theme.palette.error.dark,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  lineHeight: '24px',
                  fontWeight: '600',
                }}
              >
                Template Requires a PestPac Account
              </Typography>
            </AlertTitle>

            <Typography variant="body2">
              You must associate this record with a location before sending this
              agreement.
            </Typography>
          </Alert>
          {canCreateLocations && (
            <Box
              pt={2}
              display={'flex'}
              flexDirection={'row'}
              gap={3}
              alignItems={'center'}
            >
              <Button
                color="primary"
                variant="outlined"
                onClick={handleCreateNewLocation}
                disabled={saving}
              >
                {title}
              </Button>
              <LinkButton onClick={handleMatchLocation}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: '500',
                    lineHeight: '22px',
                    letterSpace: '0.46px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Match to Existing PestPac Location
                  <OpenNewFieldIcon />
                </Typography>
              </LinkButton>
            </Box>
          )}
          {saving && (
            <LinearProgress
              color="primary"
              sx={{ width: '100%', marginTop: theme.spacing(1) }}
            />
          )}
        </Box>
        <MatchCreateLocationDialogs
          entityId={entityId}
          entityType={entityType}
          flow={flow}
          title={title}
          tidalThemeActive={tidalThemeActive}
        />
      </>
    )
  }
  return null
}
